"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Flame, PhoneCall, ShieldCheck, Wrench, Zap, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const features = [{
  icon: Flame,
  title: "Tankless Specialists",
  description: "Factory-trained on all Rinnai high-efficiency gas and electric tankless units."
}, {
  icon: Clock,
  title: "24/7 Rapid Response",
  description: "Emergency repairs and same-day water heater replacements in Jonesboro, GA."
}, {
  icon: ShieldCheck,
  title: "Warranty Protection",
  description: "Certified installations ensuring your full Rinnai manufacturer warranty remains valid."
}];
export default function RinnaiHero() {
  const shouldReduceMotion = useReducedMotion();
  const animateProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <section id="rinnaihero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 lg:py-24 border-b border-border">
      {/* Subtle decorative background pattern */}
      <div className="absolute inset-0 bg-radial-[at_top_right] from-muted/60 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start gap-6" {...animateProps}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-semibold text-secondary-foreground flex items-center gap-1.5">
                <Flame className="size-3.5 text-accent fill-accent" />
                Rinnai Certified Specialists
              </Badge>
              <span className="text-xs font-medium text-muted-foreground">
                Jonesboro & South Metro Atlanta
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
                Certified Rinnai Water Heater Repair & Installation
              </h1>
              <p className="font-default text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Experience nonstop hot water and lower energy bills. Our licensed Jonesboro plumbers provide fast 24/7 emergency repair, preventative maintenance, and new installation for Rinnai tankless systems.
              </p>
            </div>

            {/* Quick Benefits Bullet List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg pt-1">
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Same-day diagnostic & repair</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Genuine Rinnai OEM parts</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Direct vent & combi-boiler support</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Upfront, flat-rate estimates</span>
              </div>
            </div>

            {/* CTA Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  Request Rinnai Service
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted text-foreground">
                <Link to="/service-areas/" className="flex items-center justify-center gap-2">
                  <Wrench className="size-4" />
                  View Service Areas
                </Link>
              </Button>
            </div>

            <div className="pt-2 text-xs text-muted-foreground flex items-center gap-2">
              <span className="inline-block size-2 rounded-full bg-chart-4" />
              <span>Available 24/7 for urgent water heater leaks & system outages</span>
            </div>
          </motion.div>

          {/* Media & Visual Showcase Column */}
          <motion.div className="lg:col-span-5" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rinnai_water_heater_installation.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rinnai_water_heater_installation.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/rinnai_water_heater_installation.webp-wvc-sizes" alt="Experienced plumber installing modern Rinnai tankless water heater" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover object-center" />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                
                {/* Floating Overlay Badge inside Image */}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-background/95 backdrop-blur-sm border border-border/80 shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary">
                        <Zap className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground">Infinite On-Demand Hot Water</div>
                        <div className="text-xs text-muted-foreground">Up to 40% more energy efficient</div>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="secondary" className="shrink-0 text-xs">
                      <Link to="/tankless-water-heater-service/">
                        Learn More
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Decorative side accent tag */}
              <div className="hidden sm:flex absolute -top-4 -right-4 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg items-center gap-1.5 border border-primary-foreground/20">
                <ShieldCheck className="size-3.5" />
                Rinnai PRO Certified
              </div>
            </div>
          </motion.div>

        </div>

        {/* Feature Cards Bottom Grid */}
        <div className="mt-16 pt-12 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, i) => {
          const IconComponent = feature.icon;
          return <Card key={feature.title} data-index={i} className="bg-card border-border shadow-xs hover:shadow-md transition-shadow">
                <CardContent className="p-6 flex flex-col gap-3">
                  <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground">
                    <IconComponent className="size-5" />
                  </div>
                  <h3 className="font-default font-semibold text-base text-card-foreground">
                    {feature.title}
                  </h3>
                  <p className="font-default text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}